import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from './store/authStore';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Landing } from './pages/Landing';
import { Auth } from './pages/Auth';
import { VendorDashboard } from './pages/VendorDashboard';
import { Dashboard } from './pages/Dashboard';
import { Chat } from './pages/Chat';
import { QRManagement } from './pages/QRManagement';
import { QRAnalytics } from './pages/QRAnalytics';
import { ResponseViewer } from './pages/ResponseViewer';
import { PublicFormFill } from './pages/PublicFormFill';
import { APIKeys } from './pages/APIKeys';
import { TeamManagement } from './pages/TeamManagement';
import { DemoChat } from './pages/DemoChat';
import OnboardingWizard from './pages/OnboardingWizard';
import AuthCallback from './pages/AuthCallback';
import StatusPage from './pages/StatusPage';
import AuditLog from './pages/AuditLog';
import ProfileSettings from './pages/ProfileSettings';
import AdminPanel from './pages/AdminPanel';
import { Integrations } from './pages/Integrations';
import BillingPage from './pages/BillingPage';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { reportError } from './utils/errorReporter';
import './App.css';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();

  // Synchronous check: detect OAuth callback with session_id in hash fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/fill/:code" element={<PublicFormFill />} />
      <Route path="/f/:slug" element={<PublicFormFill isSlug />} />
      <Route path="/demo" element={<DemoChat />} />
      <Route path="/system-status" element={<StatusPage />} />
      <Route
        path="/onboarding"
        element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>}
      />
      <Route
        path="/dashboard"
        element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>}
      />
      <Route
        path="/my-forms"
        element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
      />
      <Route
        path="/qr-codes"
        element={<ProtectedRoute><QRManagement /></ProtectedRoute>}
      />
      <Route
        path="/qr-codes/:qrId/analytics"
        element={<ProtectedRoute><QRAnalytics /></ProtectedRoute>}
      />
      <Route
        path="/qr-codes/:qrId/responses"
        element={<ProtectedRoute><ResponseViewer /></ProtectedRoute>}
      />
      <Route
        path="/api-keys"
        element={<ProtectedRoute><APIKeys /></ProtectedRoute>}
      />
      <Route
        path="/team"
        element={<ProtectedRoute><TeamManagement /></ProtectedRoute>}
      />
      <Route
        path="/audit-log"
        element={<ProtectedRoute><AuditLog /></ProtectedRoute>}
      />
      <Route
        path="/settings"
        element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>}
      />
      <Route
        path="/admin"
        element={<ProtectedRoute><AdminPanel /></ProtectedRoute>}
      />
      <Route
        path="/integrations"
        element={<ProtectedRoute><Integrations /></ProtectedRoute>}
      />
      <Route
        path="/billing"
        element={<ProtectedRoute><BillingPage /></ProtectedRoute>}
      />
      <Route
        path="/chat/:sessionId"
        element={<ProtectedRoute><Chat /></ProtectedRoute>}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const init = useAuthStore((s) => s.init);
  useEffect(() => {
    // CRITICAL: Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) return;
    init();
  }, [init]);

  // Global 429 rate-limit interceptor + error reporter
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 429) {
          toast.error(err.response.data?.detail || "You're sending requests too quickly. Please wait a moment.");
        }
        // Report 5xx and network errors to error_logs
        const status = err.response?.status;
        if (status >= 500 || !err.response) {
          reportError({
            errorMessage: err.message || 'Network error',
            errorType: status ? `HTTP_${status}` : 'NetworkError',
            endpoint: err.config?.url,
            method: err.config?.method?.toUpperCase(),
            statusCode: status || null,
            cause: err.response?.data?.detail || err.message,
          });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Initialize dark mode from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (saved === 'system') {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
      }
    }
  }, []);

  return (
    <>
      <ErrorBoundary>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-center" richColors />
    </>
  );
}

export default App;
